/**
 * Admin translation override editor.
 *
 * Displays every i18n key grouped by namespace for all five languages,
 * lets admins override any value, and persists changes to localStorage.
 * Overrides are picked up by the i18n configuration on next load.
 *
 * @module TranslationManagementPage
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Chip,
  Alert,
  alpha,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import { motion } from 'framer-motion';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import TranslateIcon from '@mui/icons-material/Translate';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../contexts/AuthContext';
import { GREEN, DARK_BLUE } from '../theme';
import { PageTransition, FloatingOrb } from '../components/AnimatedComponents';

import nl from '../i18n/locales/nl.json';
import en from '../i18n/locales/en.json';
import de from '../i18n/locales/de.json';
import fr from '../i18n/locales/fr.json';
import es from '../i18n/locales/es.json';

/** Available languages with display labels and flag emoji. */
const LANGUAGES = [
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
];

/** Default (bundled) translation dictionaries keyed by language code. */
const DEFAULT_TRANSLATIONS: Record<string, Record<string, unknown>> = { nl, en, de, fr, es };
/** localStorage key for persisted translation overrides. */
const STORAGE_KEY = 'booge_it_translation_overrides';

/** Flatten a nested object into dot-separated key–value pairs. */
function flattenObject(obj: Record<string, unknown>, prefix = ''): Record<string, string> {
  const result: Record<string, string> = {};
  for (const key of Object.keys(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    const val = obj[key];
    if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
      Object.assign(result, flattenObject(val as Record<string, unknown>, fullKey));
    } else {
      result[fullKey] = String(val ?? '');
    }
  }
  return result;
}

/** Extract unique top-level namespace prefixes from a list of dot-separated keys. */
function getNamespaces(keys: string[]): string[] {
  const ns = new Set<string>();
  for (const key of keys) {
    const dot = key.indexOf('.');
    if (dot > 0) ns.add(key.substring(0, dot));
  }
  return Array.from(ns).sort();
}

/** Load translation overrides from localStorage (returns empty object on failure). */
function loadOverrides(): Record<string, Record<string, string>> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return {};
}

/** Persist translation overrides to localStorage. */
function saveOverrides(overrides: Record<string, Record<string, string>>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
}

/** Translation management UI with per-language override editing and namespace grouping. */
export default function TranslationManagementPage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [search, setSearch] = useState('');
  const [expandedNs, setExpandedNs] = useState<Set<string>>(new Set());
  const [overrides, setOverrides] = useState<Record<string, Record<string, string>>>(loadOverrides);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!user || !isAdmin) {
      navigate('/login');
    }
  }, [user, isAdmin, navigate]);

  // Build flat translation maps per language
  const flatDefaults = useMemo(() => {
    const result: Record<string, Record<string, string>> = {};
    for (const lang of LANGUAGES) {
      result[lang.code] = flattenObject(DEFAULT_TRANSLATIONS[lang.code] as Record<string, unknown>);
    }
    return result;
  }, []);

  const allKeys = useMemo(() => {
    const keySet = new Set<string>();
    for (const langMap of Object.values(flatDefaults)) {
      for (const k of Object.keys(langMap)) keySet.add(k);
    }
    return Array.from(keySet).sort();
  }, [flatDefaults]);

  const namespaces = useMemo(() => getNamespaces(allKeys), [allKeys]);

  const filteredKeys = useMemo(() => {
    if (!search.trim()) return allKeys;
    const q = search.toLowerCase();
    return allKeys.filter((key) => {
      if (key.toLowerCase().includes(q)) return true;
      for (const lang of LANGUAGES) {
        const val = overrides[lang.code]?.[key] ?? flatDefaults[lang.code]?.[key] ?? '';
        if (val.toLowerCase().includes(q)) return true;
      }
      return false;
    });
  }, [search, allKeys, flatDefaults, overrides]);

  const getValue = useCallback(
    (lang: string, key: string) => overrides[lang]?.[key] ?? flatDefaults[lang]?.[key] ?? '',
    [overrides, flatDefaults]
  );

  const isModified = useCallback(
    (lang: string, key: string) => {
      const ov = overrides[lang]?.[key];
      return ov !== undefined && ov !== flatDefaults[lang]?.[key];
    },
    [overrides, flatDefaults]
  );

  const handleChange = useCallback(
    (lang: string, key: string, value: string) => {
      setOverrides((prev) => {
        const next = { ...prev };
        if (!next[lang]) next[lang] = {};
        if (value === flatDefaults[lang]?.[key]) {
          const langOv = { ...next[lang] };
          delete langOv[key];
          if (Object.keys(langOv).length === 0) {
            delete next[lang];
          } else {
            next[lang] = langOv;
          }
        } else {
          next[lang] = { ...next[lang], [key]: value };
        }
        return next;
      });
      setSaved(false);
    },
    [flatDefaults]
  );

  const handleSave = useCallback(() => {
    saveOverrides(overrides);
    // Apply overrides to i18next
    for (const lang of LANGUAGES) {
      if (overrides[lang.code]) {
        // Build nested object from flat overrides
        const nested: Record<string, unknown> = {};
        for (const [flatKey, val] of Object.entries(overrides[lang.code])) {
          const parts = flatKey.split('.');
          let cur: Record<string, unknown> = nested;
          for (let i = 0; i < parts.length - 1; i++) {
            if (!cur[parts[i]] || typeof cur[parts[i]] !== 'object') {
              cur[parts[i]] = {};
            }
            cur = cur[parts[i]] as Record<string, unknown>;
          }
          cur[parts[parts.length - 1]] = val;
        }
        i18n.addResourceBundle(lang.code, 'translation', nested, true, true);
      }
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }, [overrides, i18n]);

  const handleReset = useCallback(() => {
    if (window.confirm(t('translations.resetConfirm'))) {
      setOverrides({});
      localStorage.removeItem(STORAGE_KEY);
      // Reload page to reset i18next resources
      window.location.reload();
    }
  }, [t]);

  const handleExpandAll = () => setExpandedNs(new Set(namespaces));
  const handleCollapseAll = () => setExpandedNs(new Set());

  const toggleNs = (ns: string) => {
    setExpandedNs((prev) => {
      const next = new Set(prev);
      if (next.has(ns)) next.delete(ns);
      else next.add(ns);
      return next;
    });
  };

  const totalOverrides = Object.values(overrides).reduce(
    (sum, langOv) => sum + Object.keys(langOv).length,
    0
  );

  if (!user || !isAdmin) return null;

  return (
    <PageTransition>
      <SEO title={t('translations.title')} noindex />
      <Box
        sx={{
          py: { xs: 3, md: 5 },
          position: 'relative',
          overflow: 'hidden',
          minHeight: '80vh',
          background: `linear-gradient(180deg, ${alpha(DARK_BLUE, 0.02)} 0%, transparent 40%)`,
        }}
      >
        <FloatingOrb size={250} color={GREEN} top={-60} right={-80} animationDelay="2s" />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Tooltip title={t('admin.backToAdmin')}>
                <IconButton onClick={() => navigate('/admin')} sx={{ bgcolor: alpha(DARK_BLUE, 0.05) }}>
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${GREEN}, ${DARK_BLUE})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <TranslateIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${DARK_BLUE}, ${GREEN})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {t('translations.title')}
              </Typography>
            </Box>
            <Typography color="text.secondary" sx={{ mb: 3, fontSize: '1.05rem', ml: 8 }}>
              {t('translations.subtitle')}
            </Typography>
          </motion.div>

          {saved && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
              <Alert severity="success" sx={{ mb: 2 }}>{t('translations.saved')}</Alert>
            </motion.div>
          )}

          {/* Controls */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              border: `1px solid ${alpha(DARK_BLUE, 0.06)}`,
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <TextField
              size="small"
              placeholder={t('translations.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: 1, minWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                startIcon={<UnfoldMoreIcon />}
                onClick={handleExpandAll}
                sx={{ borderRadius: 3 }}
              >
                {t('translations.expandAll')}
              </Button>
              <Button
                size="small"
                startIcon={<UnfoldLessIcon />}
                onClick={handleCollapseAll}
                sx={{ borderRadius: 3 }}
              >
                {t('translations.collapseAll')}
              </Button>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                sx={{ borderRadius: 3 }}
              >
                {t('translations.save')} {totalOverrides > 0 && `(${totalOverrides})`}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<RestoreIcon />}
                onClick={handleReset}
                disabled={totalOverrides === 0}
                sx={{ borderRadius: 3 }}
              >
                {t('translations.reset')}
              </Button>
            </Stack>
          </Paper>

          {/* Language header */}
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 1,
              border: `1px solid ${alpha(DARK_BLUE, 0.06)}`,
              bgcolor: alpha(DARK_BLUE, 0.02),
              display: 'flex',
              gap: 1,
            }}
          >
            <Box sx={{ width: 220, flexShrink: 0, px: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t('translations.key')}</Typography>
            </Box>
            {LANGUAGES.map((lang) => (
              <Box key={lang.code} sx={{ flex: 1, minWidth: 150, px: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {lang.flag} {lang.label}
                </Typography>
              </Box>
            ))}
          </Paper>

          {/* Namespace accordions */}
          {namespaces.map((ns) => {
            const nsKeys = filteredKeys.filter((k) => k.startsWith(ns + '.'));
            if (nsKeys.length === 0) return null;

            const nsModifiedCount = nsKeys.reduce((count, key) => {
              for (const lang of LANGUAGES) {
                if (isModified(lang.code, key)) return count + 1;
              }
              return count;
            }, 0);

            return (
              <Accordion
                key={ns}
                expanded={expandedNs.has(ns)}
                onChange={() => toggleNs(ns)}
                sx={{
                  border: `1px solid ${alpha(DARK_BLUE, 0.06)}`,
                  '&:before': { display: 'none' },
                  mb: 0.5,
                }}
                elevation={0}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontWeight: 600 }}>{ns}</Typography>
                    <Chip label={`${nsKeys.length} keys`} size="small" variant="outlined" />
                    {nsModifiedCount > 0 && (
                      <Chip
                        label={`${nsModifiedCount} modified`}
                        size="small"
                        color="secondary"
                      />
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 1 }}>
                  {nsKeys.map((key) => (
                    <Box
                      key={key}
                      sx={{
                        display: 'flex',
                        gap: 1,
                        py: 0.75,
                        px: 1,
                        borderBottom: `1px solid ${alpha(DARK_BLUE, 0.04)}`,
                        '&:last-child': { borderBottom: 'none' },
                        '&:hover': { bgcolor: alpha(GREEN, 0.02) },
                      }}
                    >
                      <Box sx={{ width: 220, flexShrink: 0, pt: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                            wordBreak: 'break-all',
                          }}
                        >
                          {key}
                        </Typography>
                      </Box>
                      {LANGUAGES.map((lang) => (
                        <Box key={lang.code} sx={{ flex: 1, minWidth: 150 }}>
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            maxRows={3}
                            value={getValue(lang.code, key)}
                            onChange={(e) => handleChange(lang.code, key, e.target.value)}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                fontSize: '0.85rem',
                                ...(isModified(lang.code, key) && {
                                  bgcolor: alpha(GREEN, 0.06),
                                  '& fieldset': { borderColor: alpha(GREEN, 0.4) },
                                }),
                              },
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            );
          })}

          {filteredKeys.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }} elevation={0}>
              <Typography color="text.secondary">No translations match your search.</Typography>
            </Paper>
          )}
        </Container>
      </Box>
    </PageTransition>
  );
}
